/**
 * ChangeStatusModal — confirmation dialog for changing a record's active status.
 *
 * Delegates to the shared `AlertDialog` component — the same one MasterCrud uses
 * for its activate/deactivate confirmation (MasterCrud.tsx:1954, `type={isActive
 * ? 'danger' : 'success'}`). A prior revision hand-rolled a custom modal (icon
 * square, bordered header/footer, fixed 420px width) that didn't match; this
 * gets pixel-exact parity for free by reusing the same component.
 *
 * @module components/modals/ChangeStatusModal
 * @see FMPV-MVWC-E003-S005 I-001
 */
import React from 'react';
import { AlertDialog } from '../../../AlertDialog/AlertDialog';
import type { MasterPatternViewEntity } from '../../types/service.types';

export interface ChangeStatusModalProps<T extends MasterPatternViewEntity = MasterPatternViewEntity> {
  isOpen: boolean;
  record: T | null;
  onCancel: () => void;
  onConfirm: (id: string, newStatus: boolean) => void;
  isLoading: boolean;
  companyName?: string;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

export function ChangeStatusModal<T extends MasterPatternViewEntity = MasterPatternViewEntity>({
  isOpen,
  record,
  onCancel,
  onConfirm,
  isLoading,
  companyName,
  t,
}: ChangeStatusModalProps<T>) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  if (!record) return null;

  const isCurrentlyActive = record.isActive;
  const title = isCurrentlyActive
    ? tt('confirmDeactivateTitle', '¿Estás seguro de desactivar este registro?')
    : tt('confirmActivateTitle', '¿Estás seguro de activar este registro?');
  const baseDesc = isCurrentlyActive
    ? tt('confirmDeactivateDesc', 'El registro dejará de estar disponible.')
    : tt('confirmActivateDesc', 'El registro volverá a estar disponible.');
  const confirmText = isCurrentlyActive
    ? tt('deactivateAction', 'Desactivar')
    : tt('activateAction', 'Activar');

  const description = companyName ? (
    <>
      {baseDesc} {tt('changeStatusScopeNotePrefix', 'Solo se modificará el estado para ')}
      <strong>{companyName}</strong>.
    </>
  ) : baseDesc;

  const handleConfirm = () => {
    onConfirm(record.id, !isCurrentlyActive);
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      type={isCurrentlyActive ? 'danger' : 'success'}
      title={title}
      description={description}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      cancelText={tt('cancel', 'Cancelar')}
      confirmText={confirmText}
      isProcess={isLoading}
      data-testid="change-status-modal"
    />
  );
}
